// AD-7 Presentation Labels — frontend mirror of the authoritative
// backend registry at backend/domain/ad7_presentation_registry.py.
// A doctrine test asserts key + wording equality against the
// backend registry endpoint at test time. Do not edit without a
// paired backend change.

export const PASSPORT_CONTRACT_VERSION = "ad7_passport.v1";

export const ACTOR_ROLE_LABELS = {
  learner: "Learner",
  instructor: "Instructor",
  trainer: "Trainer",
  admin: "Administrator",
};

export const SOURCE_LEDGER_LABELS = {
  work_lessons_q6: "Lesson reflection",
  lesson_adaptations: "Adaptation record",
  debrief_question_presentations: "Adaptive follow-up",
  understanding_prompt_presentations: "Understanding prompt",
  ad2_reflections: "Reflection captured on the move",
};

export const PLACEMENT_PRIMARY_EVIDENCE = "primary_evidence";
export const PLACEMENT_EVIDENCE_DETAILS = "evidence_details";
export const PLACEMENT_HIDDEN_FROM_V1   = "hidden_from_v1";

const FINAL_ROUTE_LABELS = {
  answered_adaptive: "Response recorded",
  answered_understanding: "Response recorded",
  skipped: "Skipped calmly",
  swapped_to_general: "Returned to general reflection",
  closed_without_action: "Closed without a recorded response",
};

const OUTCOME_LABELS = {
  stayed_on_adaptation: "Stayed with the adaptation",
  returned_to_original: "Returned to the original plan",
  partial_return: "Returned in part to the original plan",
};

const SOURCE_METHOD_LABELS = {
  instructor_voice: "Voice",
  instructor_typed: "Typed",
  instructor_skipped: "Skipped",
};

const EVIDENCE_CATEGORY_LABELS = {
  homework_carry_over: "Homework continuity",
  adaptation_outcome: "Adaptation outcome",
  adaptation_other_pj: "Professional judgement",
  calm_lesson: "Calm lesson",
};

const SELECTION_REASON_LABELS = {
  homework_open_linked_objective: "Open homework on this objective",
  adaptation_other_professional_judgement: "Recorded professional judgement",
  adaptation_outcome_stayed: "Adaptation continued for the lesson",
  adaptation_outcome_returned: "Adaptation returned to the original plan",
  adaptation_outcome_partial: "Adaptation partially returned",
  understanding_outcome_stayed: "Understanding follows a continued adaptation",
  understanding_outcome_returned: "Understanding follows a returned adaptation",
  understanding_outcome_partial: "Understanding follows a partial return",
  understanding_after_calm_lesson: "Understanding after a calm lesson",
};

// FieldSpecs mirror — placement + render_rule per source per key.
// Keys align exactly with backend FIELD_SPECS. Enum maps live above.
const AD4_AD5_FIELDS = {
  presented_question_key: { label: "Presented question key", placement: PLACEMENT_HIDDEN_FROM_V1, render: "free_text", enumMap: null },
  evidence_category:      { label: "Evidence category",       placement: PLACEMENT_EVIDENCE_DETAILS, render: "enum", enumMap: EVIDENCE_CATEGORY_LABELS },
  selection_reason:       { label: "Selection reason",        placement: PLACEMENT_EVIDENCE_DETAILS, render: "enum", enumMap: SELECTION_REASON_LABELS },
  presented_at:           { label: "Presented at",            placement: PLACEMENT_EVIDENCE_DETAILS, render: "timestamp", enumMap: null },
  final_route:            { label: "Interaction outcome",     placement: PLACEMENT_PRIMARY_EVIDENCE, render: "enum", enumMap: FINAL_ROUTE_LABELS },
  route_finalised_at:     { label: "Interaction finalised at",placement: PLACEMENT_EVIDENCE_DETAILS, render: "timestamp", enumMap: null },
  registry_version:       { label: "Registry version",        placement: PLACEMENT_HIDDEN_FROM_V1, render: "free_text", enumMap: null },
  orchestration_version:  { label: "Orchestration version",   placement: PLACEMENT_HIDDEN_FROM_V1, render: "free_text", enumMap: null },
};

const STOP_REASON_LABELS = {
  user_tap: "Stopped by instructor",
  length_ceiling: "Reached 90-second ceiling",
  teardown: "App closed during capture",
  recovery_finalisation: "Recovered after interruption",
};

const AD2_FIELDS = {
  duration_seconds: { label: "Duration (seconds)", placement: PLACEMENT_PRIMARY_EVIDENCE, render: "free_text", enumMap: null },
  captured_at:      { label: "Captured at",        placement: PLACEMENT_PRIMARY_EVIDENCE, render: "timestamp", enumMap: null },
  stop_reason:      { label: "Stop reason",        placement: PLACEMENT_EVIDENCE_DETAILS, render: "enum", enumMap: STOP_REASON_LABELS },
  media_id:         { label: "Media reference",    placement: PLACEMENT_EVIDENCE_DETAILS, render: "evidence_reference", enumMap: null },
};

export const FIELD_SPECS = {
  work_lessons_q6: {
    objective_snapshot:         { label: "Lesson focus",       placement: PLACEMENT_PRIMARY_EVIDENCE, render: "free_text", enumMap: null },
    went_well:                  { label: "Positive reflection",placement: PLACEMENT_PRIMARY_EVIDENCE, render: "free_text", enumMap: null },
    end_reflection_evidence_id: { label: "Evidence reference", placement: PLACEMENT_EVIDENCE_DETAILS, render: "evidence_reference", enumMap: null },
  },
  lesson_adaptations: {
    objective_snapshot: { label: "Lesson focus", placement: PLACEMENT_PRIMARY_EVIDENCE, render: "free_text", enumMap: null },
    adapted:            { label: "Lesson was adapted", placement: PLACEMENT_PRIMARY_EVIDENCE, render: "boolean", enumMap: null },
    primary_blocker:    { label: "Recorded circumstance", placement: PLACEMENT_PRIMARY_EVIDENCE, render: "enum", enumMap: {} /* filled by backend on shipment */ },
    secondary_blockers: { label: "Additional circumstances", placement: PLACEMENT_EVIDENCE_DETAILS, render: "array_of_enum", enumMap: {} },
    adaptation:         { label: "Adaptation notes", placement: PLACEMENT_PRIMARY_EVIDENCE, render: "free_text", enumMap: null },
    educational_reason: { label: "Recorded reasoning", placement: PLACEMENT_PRIMARY_EVIDENCE, render: "free_text", enumMap: null },
    outcome:            { label: "Recorded outcome", placement: PLACEMENT_PRIMARY_EVIDENCE, render: "enum", enumMap: OUTCOME_LABELS },
    next_lesson_recommendation: { label: "Next lesson recommendation", placement: PLACEMENT_EVIDENCE_DETAILS, render: "free_text", enumMap: null },
    source:             { label: "Recording method", placement: PLACEMENT_EVIDENCE_DETAILS, render: "enum", enumMap: SOURCE_METHOD_LABELS },
  },
  debrief_question_presentations:    AD4_AD5_FIELDS,
  understanding_prompt_presentations: AD4_AD5_FIELDS,
  ad2_reflections:                    AD2_FIELDS,
};

// ── Value renderer (mirrors backend rules) ─────────────────
export function renderValue(spec, value) {
  if (value === null || value === undefined) return null;         // omit row
  switch (spec.render) {
    case "boolean":
      return value === true ? "Yes" : value === false ? "No" : null;
    case "timestamp":
      try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return null;
        return d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "UTC" });
      } catch { return null; }
    case "enum":
      return (spec.enumMap && spec.enumMap[value]) || null;
    case "array_of_enum":
      if (!Array.isArray(value)) return null;
      const parts = value.map(v => (spec.enumMap && spec.enumMap[v]) || null);
      return parts.every(Boolean) ? parts.join(", ") : null;
    case "free_text":
    case "evidence_reference":
      return String(value);
    default:
      return null;
  }
}

export function renderEvidenceReferenceTail(value) {
  if (!value) return "";
  const s = String(value);
  return s.length <= 8 ? s : `…${s.slice(-8)}`;
}
